import _ from 'lodash'
import { Module } from 'vuex'
import { RootState } from '@ps/types/store'
import Konva from 'konva'
import { selectionRectangleConfig } from './config/layer-config'
import store from '@ps/store'
import { getOffset } from '@ps/store/workbench/stage/utils/helper'
import { initAnchorsStyle } from '@com/konva-modules/transformer-style'
import eventListener from '@ps/store/workbench/stage/actions/event-listener'
import { Event, EventBus } from '@ps/utils/event-bus'
import StageData from '@ps/store/workbench/stage/stage-data'
import ruler from '@ps/store/workbench/stage/actions/ruler'
import snap from '@ps/store/workbench/stage/actions/snap'
import virtualNode from '@ps/store/workbench/stage/actions/virtual-node'
import { GeneralMode, StageState } from '@ps/types/workbench/stage.type'
import { transformerConfig } from '@ps/store/workbench/stage/config/basic-config'
import { getDrawLineInScene } from '@ps/bridge/qc/base'
import { toQcColor } from '@ps/bridge/qc/color'
import gameIframe from '@ps/bridge/iframe/iframe'

const stageModule: Module<StageState, RootState> = {
  namespaced: true,

  state: {
    id: '',
    parentId: '',
    sceneId: '',
    sceneOffset: {
      x: 0,
      y: 0,
    },
    preStyle: [],
    afterStyle: [],
    showRuler: true,
    allowSnapping: true,
    isFocusin: false,
    isTransform: false,
    mode: {
      drawLine: GeneralMode.None,
      drawLineArea: GeneralMode.None,
    },
  },

  mutations: {
    // 添加辅助图层
    addSelectionNode() {
      if (!StageData.layer || !StageData.stage) {
        console.error('当前画布没有 layer / stage')
        return
      }
      const KonvaLayer = StageData.layer as Konva.Layer

      StageData.transformer = new Konva.Transformer(transformerConfig)
      StageData.selectionRectangle = new Konva.Rect(selectionRectangleConfig)

      KonvaLayer.add(StageData.transformer)
      initAnchorsStyle(StageData.transformer)
      KonvaLayer.add(StageData.selectionRectangle)
    },

    clearSelection() {
      const KonvaTransformer = StageData.transformer as Konva.Transformer
      KonvaTransformer.nodes([])
    },

    updateSceneOffset(state) {
      state.sceneOffset = getOffset(state.parentId, state.sceneId)
    },

    updateShowRulerStatus(state, status: boolean) {
      state.showRuler = status
    },

    updateAllowSnappingStatus(state, status: boolean) {
      state.allowSnapping = status
    },

    addActivationShape(state, shape: Konva.Rect) {
      StageData.activationShape.push(shape)
    },

    toggleActivationShapeVisible(state, visible: boolean) {
      if (StageData.activationShape.length) {
        StageData.activationShape.forEach((shape) => {
          shape.visible(visible)
        })
        if (visible) {
          StageData.activationShape = []
        }
      }
    },

    updateDrawLineMode(state, mode: GeneralMode) {
      state.mode.drawLine = mode
      const currentSceneNode =
        store.state.workspace.sceneManager.currentSceneNode
      const drawLineNode = getDrawLineInScene(currentSceneNode)
      if (drawLineNode) {
        // 选中连线组件时，设置白色滤镜，透明度0.5是为了显示层级靠下的元素，也能让预览线条不那么透明
        if (mode === GeneralMode.Editable) {
          drawLineNode.alpha = 0.5
          drawLineNode.colorTint = toQcColor('rgb(255,255,255)')
        } else {
          // 关闭预览线条bitmapdata.clear()后不能显示texture了，需要置空再重置texture才能显示
          // @ts-ignore
          drawLineNode.texture = null
          const res = gameIframe.iframe.qc_game.assets.find(
            '__builtin_resource__',
          )
          const emptyTexture = res.getTexture('empty.png')
          drawLineNode.texture = emptyTexture
          drawLineNode.alpha = 0.3
          drawLineNode.colorTint = toQcColor('rgba(82, 196, 26, 0.3)')
        }
      }
      EventBus.$emit(Event.REFRESH_STAGE)
    },

    updateDrawLineAreaMode(state, mode: GeneralMode) {
      state.mode.drawLineArea = mode
      EventBus.$emit(Event.REFRESH_STAGE)
    },
  },

  actions: {
    /**
     * 初始化画布
     * @param state
     * @param dispatch
     * @param commit
     * @param id 画布 id
     * @param parentId 父节点 id， 画布的大小会跟随父节点的大小变化，确保画布的大小都为最大的
     * @param sceneId 场景 id，用于获取场景的大小和以父级点的偏移值，用于计算场景中元素处于画布的位置
     */
    initStage(
      { state, commit, dispatch },
      {
        id,
        parentId,
        sceneId,
      }: {
        id: string
        parentId: string
        sceneId: string
      },
    ) {
      if (!id || !parentId || !sceneId) {
        console.error('id 参数必填')
        return
      }
      if (StageData.stage) {
        console.error('重复实例化')
        return
      }
      state.id = id
      state.parentId = parentId
      state.sceneId = sceneId

      const sceneContainer = document.querySelector(
        `#${parentId}`,
      ) as HTMLElement
      const stageWidth = sceneContainer?.offsetWidth
      const stageHeight = sceneContainer?.offsetHeight

      StageData.stage = new Konva.Stage({
        container: id,
        width: stageWidth,
        height: stageHeight,
      })

      const KonvaStage = StageData.stage as Konva.Stage
      StageData.layer = new Konva.Layer()
      StageData.rulerLayer = new Konva.Layer()
      KonvaStage.add(StageData.layer, StageData.rulerLayer)
      StageData.layer.zIndex(0)
      StageData.rulerLayer.zIndex(1)
      commit('addSelectionNode')
      commit('updateSceneOffset')
      dispatch('addSelectionListener')
      dispatch('drawKonvaRuler')
      dispatch('addLayerSnappingEvent')
      /**
       * 历史是绑定节点的 onTransformChanged 事件，会触发所有节点的 ShapeLayout
       * 为什么要触发所有节点的 ShapeLayout？
       * 因为游戏树里面 parent 节点的 x/y/scale 等属性调整时，会直接影响 child 节点的位置大小
       * 因此，在改动节点的宽高之后，还需要更新节点的子节点的 layout 数据
       * 为什么不可以绑定 onTransformChanged 事件
       * 文本节点会每一帧触发 onTransformChanged ，直接导致每一帧都会触发事件
       * 只是从 0-1 的项目中，节点数量不多，不会达到瓶颈；
       * 当是自研项目时，节点众多，并且都会同时触发所有节点的 resetShapeLayout, 会导致浏览器卡顿
       * 因此，直接每一帧更新所有 shapeLayout
       */
      const loop = async () => {
        await dispatch('resetAllShapeLayout')
        window.requestAnimationFrame(loop)
      }
      window.requestAnimationFrame(loop)
    },

    /**
     * 重新更新画布大小
     * @param state
     * @param dispatch
     */
    resizeStage({ state, dispatch }) {
      if (StageData.stage) {
        const sceneContainer = document.querySelector(
          `#${state.parentId}`,
        ) as HTMLElement
        const stageWidth = sceneContainer?.offsetWidth
        const stageHeight = sceneContainer?.offsetHeight
        const KonvaStage = StageData.stage as Konva.Stage
        KonvaStage.width(stageWidth)
        KonvaStage.height(stageHeight)
        // 确保 scale 事件先执行
        setTimeout(() => {
          dispatch('drawKonvaRuler')
        })
      }
    },

    resizeStageByConfig(
      { commit, dispatch },
      {
        width,
        height,
        showRuler,
        allowSnapping,
      }: {
        width?: number | string
        height?: number | string
        showRuler?: boolean
        allowSnapping?: boolean
      },
    ) {
      if (StageData.stage) {
        if (typeof width !== 'undefined' && typeof height !== 'undefined') {
          store.commit('workspace/game/device/updateSceneSize', {
            width,
            height,
          })
        } else {
          const isSingleScene =
            store.getters['workspace/storyboard/isSingleScene']
          store.dispatch(
            'workspace/game/device/updateSceneSizeByDeviceSize',
            !isSingleScene,
          )
        }
        if (typeof showRuler !== 'undefined') {
          commit('updateShowRulerStatus', showRuler)
        }
        if (typeof allowSnapping !== 'undefined') {
          commit('updateAllowSnappingStatus', allowSnapping)
        }
        setTimeout(() => {
          commit('updateSceneOffset')
          dispatch('resizeStage')
        })
      }
    },

    // 通过 preStyle 与 afterStyle 更新 node 的样式
    updateNodeStyle: _.debounce(async ({ state }) => {
      await store.dispatch('workspace/game/setStyle', {
        preNodes: state.preStyle,
        nodes: state.afterStyle,
        record: true,
      })
      EventBus.$emit(Event.CAPTUREGAME)
      // 如果是组元素，或者组内元素操作都会影响到画布布局，操作结束需要更新所有节点的位置
      state.preStyle = []
      state.afterStyle = []
    }, 100),

    ...virtualNode,
    ...eventListener,
    ...ruler,
    ...snap,
  },
}

export default stageModule
